import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Shield,
  Save,
  Lock,
  Eye,
  EyeOff,
  MapPin,
  KeyRound
} from 'lucide-react';
import api from '../services/api';

const Profile = () => {
  const queryClient = useQueryClient();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Phone change state
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [phoneStep, setPhoneStep] = useState('send'); // 'send' | 'verify'
  const [newPhone, setNewPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // Fetch current user profile
  const { data: userData, isLoading } = useQuery('currentUser', async () => {
    const response = await api.get('/auth/me');
    return response.data.user;
  });

  // Profile form state — excludes mobile_number (handled separately when already set)
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    mobile_number: '',
    department: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (userData) {
      setProfileData({
        fullName: userData.fullName || '',
        email: userData.email || '',
        mobile_number: userData.mobile_number || '',
        department: userData.department || ''
      });
    }
  }, [userData]);

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (data) => {
      const response = await api.put('/auth/profile', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('currentUser');
        toast.success('Profile updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update profile');
      }
    }
  );

  // Change password mutation
  const changePasswordMutation = useMutation(
    async (data) => {
      const response = await api.post('/auth/change-password', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to change password');
      }
    }
  );

  // Send OTP to current phone mutation
  const sendPhoneOTPMutation = useMutation(
    () => api.post('/auth/phone/send-otp'),
    {
      onSuccess: (res) => {
        toast.success(res.data.message);
        setPhoneStep('verify');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to send OTP');
      }
    }
  );

  // Verify OTP and change phone mutation
  const changePhoneMutation = useMutation(
    (data) => api.put('/auth/phone/change', data),
    {
      onSuccess: (res) => {
        toast.success(res.data.message);
        queryClient.invalidateQueries('currentUser');
        resetPhoneForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to change phone number');
      }
    }
  );

  const resetPhoneForm = () => {
    setShowPhoneForm(false);
    setPhoneStep('send');
    setNewPhone('');
    setOtpCode('');
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    // If user already has a phone number, exclude it from this update
    const dataToSubmit = userData?.mobile_number
      ? { fullName: profileData.fullName, email: profileData.email, department: profileData.department }
      : profileData;
    updateProfileMutation.mutate(dataToSubmit);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword
    });
  };

  const handlePhoneVerify = (e) => {
    e.preventDefault();
    if (!newPhone.match(/^[0-9]{10,15}$/)) {
      toast.error('Enter a valid 10-15 digit phone number');
      return;
    }
    if (!otpCode.trim()) {
      toast.error('Enter the OTP');
      return;
    }
    changePhoneMutation.mutate({ newPhone, otpCode });
  };

  const getRoleBadge = (role) => {
    const badges = {
      dc: { color: 'bg-purple-100 text-purple-800', label: 'DC' },
      adc: { color: 'bg-indigo-100 text-indigo-800', label: 'ADC' },
      sdo: { color: 'bg-blue-100 text-blue-800', label: 'SDO' },
      eac: { color: 'bg-cyan-100 text-cyan-800', label: 'EAC' },
      bo: { color: 'bg-green-100 text-green-800', label: 'Branch Officer' }
    };
    const badge = badges[role] || badges.bo;
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const hasPhone = !!userData?.mobile_number;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">My Profile</h1>
        <p className="text-secondary-600 mt-1">Manage your account information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Summary Card */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col items-center">
              <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-3xl">
                  {userData?.fullName?.charAt(0).toUpperCase()}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-secondary-900 text-center">
                {userData?.fullName}
              </h2>
              <p className="text-secondary-500 text-sm mb-3">@{userData?.username}</p>
              {getRoleBadge(userData?.role)}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-secondary-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Email</p>
                  <p className="text-sm text-secondary-900 break-words">{userData?.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-secondary-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Mobile</p>
                  <p className="text-sm text-secondary-900">{userData?.mobile_number || '-'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Briefcase className="h-5 w-5 text-secondary-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Designation</p>
                  <p className="text-sm text-secondary-900">{userData?.designation || '-'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Building2 className="h-5 w-5 text-secondary-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Department</p>
                  <p className="text-sm text-secondary-900">{userData?.department || '-'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-secondary-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Subdivision</p>
                  {userData?.subdivision ? (
                    <p className="text-sm text-secondary-900">
                      {userData.subdivision.name}
                      {userData.role === 'adc' && (
                        <span className="ml-2 text-xs text-blue-600">(Head)</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-secondary-500 italic">
                      {userData?.role === 'dc' ? 'District-wide access' :
                       userData?.role === 'adc' ? 'District-wide access' :
                       'Not assigned'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-medium text-secondary-900 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Profile Information
              </h3>
              <p className="text-sm text-secondary-500 mt-1">
                Update your account profile information
              </p>
            </div>
            <form onSubmit={handleProfileSubmit} className="px-6 py-4">
              <div className="space-y-4">
                {/* Read-only fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Username (Read-only)
                    </label>
                    <input
                      type="text"
                      disabled
                      value={userData?.username || ''}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-secondary-500 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Role (Read-only)
                    </label>
                    <div className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-secondary-500">
                      {getRoleBadge(userData?.role)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Designation (Read-only)
                    </label>
                    <input
                      type="text"
                      disabled
                      value={userData?.designation || '-'}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-secondary-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Editable fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={profileData.fullName}
                      onChange={(e) =>
                        setProfileData({ ...profileData, fullName: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={profileData.email}
                      onChange={(e) =>
                        setProfileData({ ...profileData, email: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Mobile number: editable only if not yet set */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Mobile Number {!hasPhone && <span className="text-red-500">*</span>}
                    </label>
                    {hasPhone ? (
                      <div>
                        <input
                          type="tel"
                          disabled
                          value={userData.mobile_number}
                          className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-secondary-500 cursor-not-allowed"
                        />
                        <p className="text-xs text-secondary-500 mt-1">
                          To change, use <span className="font-medium">Change Phone Number</span> below.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="tel"
                          required
                          pattern="[0-9]{10,15}"
                          maxLength="15"
                          value={profileData.mobile_number}
                          onChange={(e) =>
                            setProfileData({ ...profileData, mobile_number: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="9876543210"
                        />
                        <p className="text-xs text-secondary-500 mt-1">Enter 10-15 digit mobile number</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      value={profileData.department}
                      onChange={(e) =>
                        setProfileData({ ...profileData, department: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Land Administration"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-secondary-200">
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isLoading}
                    className="btn btn-primary"
                  >
                    {updateProfileMutation.isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>

                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-secondary-400 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900">Read-only fields</p>
                      <p className="text-xs text-secondary-600 mt-1">
                        Username, Role, and Designation cannot be changed. Contact your administrator if these need to be updated.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Change Phone Number — only shown if user already has a number */}
          {hasPhone && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-secondary-900 flex items-center">
                      <Phone className="h-5 w-5 mr-2" />
                      Change Phone Number
                    </h3>
                    <p className="text-sm text-secondary-500 mt-1">
                      Verify your current number via OTP before changing
                    </p>
                  </div>
                  {!showPhoneForm && (
                    <button
                      onClick={() => setShowPhoneForm(true)}
                      className="btn btn-outline"
                    >
                      Change Number
                    </button>
                  )}
                </div>
              </div>

              {showPhoneForm && (
                <div className="px-6 py-4 space-y-4">
                  {phoneStep === 'send' && (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <KeyRound className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-800">
                            An OTP will be sent to your current number ending in{' '}
                            <strong>…{userData.mobile_number.slice(-4)}</strong>. Enter it along with your new number to complete the change.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => sendPhoneOTPMutation.mutate()}
                          disabled={sendPhoneOTPMutation.isLoading}
                          className="btn btn-primary"
                        >
                          {sendPhoneOTPMutation.isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending OTP…
                            </>
                          ) : (
                            'Send OTP'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={resetPhoneForm}
                          className="btn btn-outline"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}

                  {phoneStep === 'verify' && (
                    <form onSubmit={handlePhoneVerify} className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                        <p className="text-sm text-green-800">
                          OTP sent to …{userData.mobile_number.slice(-4)}. Enter it below along with your new number.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            OTP <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength="6"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="6-digit OTP"
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 tracking-widest text-center text-lg font-mono"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            New Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            maxLength="15"
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))}
                            placeholder="10-15 digit number"
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2 border-t border-secondary-200">
                        <button
                          type="submit"
                          disabled={changePhoneMutation.isLoading}
                          className="btn btn-primary"
                        >
                          {changePhoneMutation.isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Updating…
                            </>
                          ) : (
                            'Confirm Change'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPhoneStep('send'); setOtpCode(''); setNewPhone(''); }}
                          className="btn btn-outline"
                          disabled={changePhoneMutation.isLoading}
                        >
                          Resend OTP
                        </button>
                        <button
                          type="button"
                          onClick={resetPhoneForm}
                          className="btn btn-outline"
                          disabled={changePhoneMutation.isLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Change Password Section */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-secondary-900 flex items-center">
                    <Lock className="h-5 w-5 mr-2" />
                    Security
                  </h3>
                  <p className="text-sm text-secondary-500 mt-1">
                    Change your password to keep your account secure
                  </p>
                </div>
                {!showPasswordForm && (
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="btn btn-outline"
                  >
                    Change Password
                  </button>
                )}
              </div>
            </div>

            {showPasswordForm && (
              <form onSubmit={handlePasswordSubmit} className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Current Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        required
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, currentPassword: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        minLength="6"
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, newPassword: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10"
                        placeholder="Minimum 6 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Confirm New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      minLength="6"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Re-enter new password"
                    />
                  </div>

                  <div className="pt-4 border-t border-secondary-200 flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                      className="btn btn-outline"
                      disabled={changePasswordMutation.isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={changePasswordMutation.isLoading}
                      className="btn btn-primary"
                    >
                      {changePasswordMutation.isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Changing...
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
